import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, Inbox, Mail, X, Archive } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/App';

const STATUS = {
  new: { text: 'Waiting on you', cls: 'bg-amber-500/20 text-amber-300' },
  invited: { text: 'Invited', cls: 'bg-emerald-500/20 text-emerald-300' },
  declined: { text: 'Declined', cls: 'bg-red-500/20 text-red-300' },
  archived: { text: 'Archived', cls: 'bg-gray-500/20 text-gray-400' },
};

/**
 * Admin · Requests — people asking for a space, from the in-app "available" card and the public
 * enquiry form. These were being stored with no screen to read them from.
 */
export default function AdminAdRequests({ onInvite }) {
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
  const [requests, setRequests] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/ad-invitations/requests?status=${showAll ? 'all' : 'open'}`, { headers });
      setRequests(res.data.requests || []);
    } catch (err) {
      toast.error('Could not load advertising requests');
    } finally { setLoading(false); }
  }, [showAll]);

  useEffect(() => { load(); }, [load]);

  const mark = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/ad-invitations/requests/${id}`, { status }, { headers });
      load();
    } catch (err) { toast.error('That did not save'); }
  };

  return (
    <div className="space-y-3" data-testid="admin-ad-requests">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm text-gray-400">
          Nothing here goes live on its own — you decide who gets offered a space.
        </p>
        <button className="text-xs text-amber-300 hover:text-amber-200 underline"
          onClick={() => setShowAll((v) => !v)} data-testid="ad-requests-toggle-all">
          {showAll ? 'Show only open requests' : 'Show every request'}
        </button>
      </div>

      {loading ? (
        <Loader2 className="h-5 w-5 animate-spin text-amber-300" />
      ) : requests.length === 0 ? (
        <div className="rounded-xl border border-white/10 bg-white/5 py-8 text-center text-gray-500"
          data-testid="ad-requests-empty">
          <Inbox className="w-7 h-7 mx-auto mb-2 opacity-30" />
          No requests {showAll ? 'yet' : 'waiting'}.
        </div>
      ) : (
        <div className="space-y-2" data-testid="ad-requests-list">
          {requests.map((r) => {
            const badge = STATUS[r.status] || STATUS.new;
            return (
              <div key={r.id}
                className="rounded-xl border border-white/10 bg-white/5 p-4 space-y-2"
                data-testid={`ad-request-${r.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <p className="text-sm text-white">
                      {r.name}
                      {r.company ? <span className="text-gray-400"> · {r.company}</span> : null}
                    </p>
                    <p className="text-xs text-gray-400">
                      {r.email} · {r.slot_label} ·{' '}
                      {r.source === 'in_app' ? 'asked from inside the app' : 'from the rate card page'} ·{' '}
                      {String(r.created_at).slice(0, 10)}
                    </p>
                  </div>
                  <Badge className={badge.cls}>{badge.text}</Badge>
                </div>

                {r.message && (
                  <p className="text-sm text-gray-300 bg-black/20 rounded-lg p-3 whitespace-pre-line">
                    {r.message}
                  </p>
                )}

                <div className="flex items-center gap-2 flex-wrap">
                  <Button size="sm" className="h-8 bg-amber-500 text-slate-900 hover:bg-amber-400"
                    onClick={() => { onInvite?.(r); mark(r.id, 'invited'); }}
                    data-testid={`ad-request-invite-${r.id}`}>
                    <Mail className="w-3.5 h-3.5 mr-1" /> Invite them to advertise
                  </Button>
                  <Button size="sm" variant="outline"
                    className="h-8 border-red-500/40 text-red-300 hover:bg-red-500/10"
                    onClick={() => mark(r.id, 'declined')}
                    data-testid={`ad-request-decline-${r.id}`}>
                    <X className="w-3.5 h-3.5 mr-1" /> Decline
                  </Button>
                  <Button size="sm" variant="ghost" className="h-8 text-gray-400"
                    onClick={() => mark(r.id, 'archived')}
                    data-testid={`ad-request-archive-${r.id}`}>
                    <Archive className="w-3.5 h-3.5 mr-1" /> Archive
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
